import BigNumber from 'bignumber.js'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { provider } from 'web3-core'
import { useWallet } from 'use-wallet'
import Card from '../../../components/Card'
import CardContent from '../../../components/CardContent'
import Label from '../../../components/Label'
import Spacer from '../../../components/Spacer'
import Value from '../../../components/Value'
import useStakedBLOKBalance from '../../../hooks/useStakedBLOKBalance'
import useTotalLPInStakingContract from '../../../hooks/useTotalLPInStakingContract'
import useAllInfo from '../../../hooks/useAllInfo'
import useKira from '../../../hooks/useKira'
import { getBalanceNumber, getDisplayBalance } from '../../../utils/formatBalance'
import useEarnings from '../../../hooks/useEarnings'
import useBLOKPrice from '../../../hooks/useBLOKPrice'
import useAllowedStakeIncrement from '../../../hooks/useAllowedStakeIncrement'
import useEstimatedReward from '../../../hooks/useEstimatedReward'

const Balances: React.FC = () => {
  const kira = useKira()
  const { account }: { account: string; ethereum: provider } = useWallet()

  const [lockedUserBalance, setLockedUserBalance] = useState(new BigNumber(0))
  const [valueOfLockedAssets, setValueOfLockedAssets] = useState(new BigNumber(0))
  const [totalLiquidityValue, setTotalLiquidityValue] = useState(new BigNumber(0))
  const [totalCirculatingLP, setTotalCirculatingLP] = useState(new BigNumber(0))
  const earnings = useEstimatedReward()

  const totalLPInStakingContract = useTotalLPInStakingContract()   // TOTAL LP TOKEN AMOUNT LOCKED IN STAKING CONTRACT
  const stakedLPBalance = useStakedBLOKBalance()          // USER'S LP TOKEN AMOUNT LOCED IN STAKING CONTRACT
  const blokPrice = useBLOKPrice()
  const allInfo = useAllInfo()
  const increments = useAllowedStakeIncrement(0);

  useEffect(() => {
    console.log(` Total BLOK Tokens in pool: ${totalLPInStakingContract}`);
    console.log(` Current user's staked BLOK balance: ${stakedLPBalance}`);
  }, [stakedLPBalance, totalLPInStakingContract])

  useEffect(() => {
    if (kira && allInfo[0]) {
      let percentOfUserBalance = totalLPInStakingContract && stakedLPBalance ? stakedLPBalance.dividedBy(totalLPInStakingContract) : 0;
      // console.log("Total WETH Value: ", allInfo[0] && allInfo[0].totalWethValue)
      console.log("       BLOK Price: ", blokPrice)
      let lockedWethValue = allInfo[0] && allInfo[0].totalWethValue.multipliedBy(percentOfUserBalance)
      setLockedUserBalance(lockedWethValue.multipliedBy(blokPrice))
      setValueOfLockedAssets(allInfo[0] && allInfo[0].totalWethValue.multipliedBy(blokPrice))
      setTotalLiquidityValue(allInfo[0] && allInfo[0].totalLiquidity.multipliedBy(blokPrice))
      setTotalCirculatingLP(allInfo[0] && allInfo[0].lpAmountInPool)
    }
  }, [kira, allInfo, stakedLPBalance, totalLPInStakingContract, blokPrice])

  return (
    <StyledWrapper>
      {!account ? ( // WALLET NOT CONNECTED
       <Card>
        <CardContent>
          <StyledCardContainer>
            <Label text="Reward Program" weight={600} size={20}/>
            <Spacer size="sm"/>

            <StyledInfoContainer>
              <Label text="- Max BLOK Stakeable" color='#FFF'/>
              <StyledInfoValue>
                <Value
                  value={4800000}
                  decimals={0}
                />
                <Label text=" BLOK" color='#9C28EC'/>
              </StyledInfoValue>
            </StyledInfoContainer>

            <StyledInfoContainer>
              <Label text="- Share size" color='#FFF'/>
              <StyledInfoValue>
                <Value
                  value={4800}
                  decimals={0}
                />
                <Label text=" BLOK" color='#9C28EC'/>
              </StyledInfoValue>
            </StyledInfoContainer>
          </StyledCardContainer>
        </CardContent>
        <Footnote>
          Total Locked BLOK Tokens
          <FootnoteValue>
            {getDisplayBalance(totalLPInStakingContract, 18)} BLOK
          </FootnoteValue>
        </Footnote>
      </Card>
      ) : (   // WALLET CONNECTED
        <Card>
          <CardContent>
            <StyledCardContainer>
              <Label text="User Information" weight={600} size={20}/>
              <Spacer size="sm"/>
              <StyledInfoContainer>
                <Label text="- Your Value Locked" color='#FFF'/>
                <StyledInfoValue>
                  <Label text="$" color='#9C28EC'/>
                  <Value
                    value={getBalanceNumber(stakedLPBalance, 18) * blokPrice}
                    decimals={2}
                  />
                </StyledInfoValue>
              </StyledInfoContainer>

              <StyledInfoContainer>
                <Label text="- Your Current Reward" color='#FFF'/>
                <StyledInfoValue>
                  <Value
                    value={getBalanceNumber(earnings)}
                    decimals={2}
                  />
                  <Label text="BLOK"/>
                </StyledInfoValue>
              </StyledInfoContainer>
            </StyledCardContainer>
          </CardContent>
          <Footnote>
            Your Locked BLOK Tokens
            <FootnoteValue>
              {getDisplayBalance(stakedLPBalance, 18)} BLOK
            </FootnoteValue>
          </Footnote>
        </Card>
      )}

      <Spacer />

      {!account ? ( // WALLET NOT CONNECTED
        <Card>
          <CardContent>
            <StyledCardContainer>
              <Label text="JOBE Information" weight={600} size={20}/>
              <Spacer size="sm"/>

              <StyledInfoContainer>
                <Label text="- Total BLOK Value Locked" color='#FFF'/>
                <StyledInfoValue>
                  <Label text="$" color='#9C28EC'/>
                  <Value
                    value={totalLPInStakingContract.toNumber() * totalLiquidityValue.toNumber()}
                  />
                </StyledInfoValue>
              </StyledInfoContainer>

              <StyledInfoContainer>
                <Label text="- BLOK Token Price" color='#FFF'/>
                <StyledInfoValue>
                  <Label text="$ " color='#9C28EC'/>
                  <Value
                    value={blokPrice}
                    decimals={5}
                  />
                </StyledInfoValue>
              </StyledInfoContainer>
            </StyledCardContainer>
          </CardContent>
          <Footnote>
            <FootnoteValue>
              &nbsp;
            </FootnoteValue>
          </Footnote>
        </Card>
      ) : (  // WALLET CONNECTED
        <Card>
          <CardContent>
            <StyledCardContainer>
              <Label text="Reward Program" weight={600} size={20}/>
              <Spacer size="sm"/>

              <StyledInfoContainer>
                <Label text="- Total Value Locked" color='#FFF'/>
                <StyledInfoValue>
                  <Label text="$" color='#9C28EC'/>
                  <Value
                    value={getBalanceNumber(totalLPInStakingContract, 18) * blokPrice}
                    decimals={2}
                  />
                </StyledInfoValue>
              </StyledInfoContainer>

              <StyledInfoContainer>
                <Label text="- Share size" color='#FFF'/>
                <StyledInfoValue>
                  <Value
                    value={getBalanceNumber(increments, 18)}
                    decimals={0}
                  />
                  <Label text=" BLOK" color='#9C28EC'/>
                </StyledInfoValue>
              </StyledInfoContainer>
            </StyledCardContainer>
          </CardContent>
          <Footnote>
            Total Locked BLOK Tokens
            <FootnoteValue>
              {getDisplayBalance(totalLPInStakingContract, 18)} BLOK
            </FootnoteValue>
          </Footnote>
        </Card>
      )}
    </StyledWrapper>
  )
}

const Footnote = styled.div`
  font-size: 14px;
  padding: 8px 20px;
  color: ${(props) => props.theme.color.purple[400]};
  border-top: solid 1px ${(props) => props.theme.color.purple[300]};
`
const FootnoteValue = styled.div`
  font-family: 'Roboto Mono', monospace;
  float: right;
  color: ${(props) => props.theme.color.purple[600]};
`

const StyledWrapper = styled.div`
  align-items: center;
  display: flex;
  @media (max-width: 768px) {
    width: 100%;
    flex-flow: column nowrap;
    align-items: stretch;
  }
`

const StyledCardContainer = styled.div`
  flex: 1
`

const StyledInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledInfoValue = styled.div`
  font-size: 20px;
  font-weight: 300;
  color: ${(props) => props.theme.color.purple[600]};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
`
export default Balances
